import { AlertTriangle, FileText, Upload } from "lucide-react";
import { useMemo, useState } from "react";
import BONavigation from "../../../components/admin/BONavigation";
import { importEstablishmentsPeopleCsv, ImportEstablishmentsPeopleCsvResponse } from "../../../interfaces/adminImport";

export default function ImportEstablishmentsPeopleCsv() {
    const [file, setFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ImportEstablishmentsPeopleCsvResponse | null>(null);

    const fileLabel = useMemo(() => {
        if (!file) return "Aucun fichier sélectionné";
        const sizeKb = Math.round(file.size / 1024);
        return `${file.name} (${sizeKb} KB)`;
    }, [file]);

    const onPickFile = (picked: File | null) => {
        setError(null);
        setResult(null);
        setFile(picked);
    };

    const onUpload = async () => {
        if (!file) return;
        try {
            setUploading(true);
            setError(null);
            const response = await importEstablishmentsPeopleCsv(file);
            setResult(response);
        } catch (err: any) {
            console.error("CSV import failed:", err);
            setError(err?.response?.data?.error || err?.message || "Import failed");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                <div className="mb-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-semibold text-black dark:text-white">Import CSV</h1>
                            <p className="text-xs text-gray-600 mt-0.5">
                                Uploadez un CSV pour créer/lier des People à des Establishments
                            </p>
                        </div>
                    </div>
                </div>

                <BONavigation />

                <div className="bg-white border border-gray-200 rounded-xs p-4 mb-6">
                    <div
                        className={`rounded-xs border-2 border-dashed p-6 text-center transition-colors ${
                            isDragging ? "border-gray-700 bg-gray-50" : "border-gray-200"
                        }`}
                        onDragEnter={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragging(true);
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragging(true);
                        }}
                        onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragging(false);
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsDragging(false);

                            const dropped = e.dataTransfer.files?.[0];
                            if (dropped) onPickFile(dropped);
                        }}
                    >
                        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                            <Upload className="h-5 w-5 text-gray-600" />
                        </div>

                        <p className="text-sm font-medium text-gray-900">Glissez-déposez votre CSV ici</p>
                        <p className="text-xs text-gray-500 mt-1">ou</p>

                        <label className="mt-3 inline-flex cursor-pointer items-center justify-center rounded-xs border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-900 hover:bg-[#F9F6F3]">
                            Choisir un fichier
                            <input
                                type="file"
                                accept=".csv,text/csv"
                                className="hidden"
                                onChange={(e) => onPickFile(e.target.files?.[0] || null)}
                            />
                        </label>

                        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-600">
                            <FileText className="h-4 w-4 text-gray-400" />
                            <span>{fileLabel}</span>
                        </div>

                        <div className="mt-4">
                            <button
                                onClick={onUpload}
                                disabled={!file || uploading}
                                className={`rounded-xs px-4 py-2 text-xs font-semibold text-white ${
                                    !file || uploading ? "bg-gray-300" : "bg-black hover:bg-gray-900"
                                }`}
                            >
                                {uploading ? "Import en cours..." : "Lancer l'import"}
                            </button>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="bg-white border border-red-200 rounded-xs p-4 mb-6">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-red-600" />
                            <p className="text-xs text-red-600">{error}</p>
                        </div>
                    </div>
                )}

                {result && (
                    <>
                        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-5">
                            <div className="bg-white border border-gray-200 rounded-xs p-4">
                                <p className="text-xs text-gray-600 mb-1">Rows</p>
                                <p className="text-xl font-semibold text-navy-700 dark:text-white">
                                    {result.processedRows}/{result.totalRows}
                                </p>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-xs p-4">
                                <p className="text-xs text-gray-600 mb-1">Created</p>
                                <p className="text-xl font-semibold text-navy-700 dark:text-white">
                                    {result.created.establishments + result.created.people + result.created.links}
                                </p>
                                <p className="mt-1 text-[11px] text-gray-500">
                                    Establishments {result.created.establishments} · People {result.created.people} · Links {result.created.links}
                                </p>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-xs p-4">
                                <p className="text-xs text-gray-600 mb-1">Updated</p>
                                <p className="text-xl font-semibold text-navy-700 dark:text-white">
                                    {result.updated.establishments + result.updated.people}
                                </p>
                                <p className="mt-1 text-[11px] text-gray-500">
                                    Establishments {result.updated.establishments} · People {result.updated.people}
                                </p>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-xs p-4">
                                <p className="text-xs text-gray-600 mb-1">Skipped</p>
                                <p className="text-xl font-semibold text-navy-700 dark:text-white">
                                    {result.skippedRows}
                                </p>
                            </div>
                            <div className="bg-white border border-gray-200 rounded-xs p-4">
                                <p className="text-xs text-gray-600 mb-1">Errors</p>
                                <p className="text-xl font-semibold text-navy-700 dark:text-white">
                                    {result.errors.length}
                                </p>
                            </div>
                        </div>

                        {result.skipped.length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-xs p-4 mb-6">
                                <h2 className="text-sm font-semibold text-navy-700 dark:text-white mb-3">
                                    Skipped rows (premières {Math.min(result.skipped.length, 50)})
                                </h2>
                                <div className="space-y-2">
                                    {result.skipped.slice(0, 50).map((e) => (
                                        <div key={`${e.rowNumber}-${e.message}`} className="rounded-xs border border-amber-200 bg-amber-50 p-3">
                                            <p className="text-xs font-medium text-amber-800">Row {e.rowNumber}</p>
                                            <p className="text-xs text-amber-800 mt-0.5">{e.message}</p>
                                            <p className="text-[11px] text-amber-900/80 mt-1">
                                                Établissement: {e.context?.establishment?.name || "—"} · SIRET {e.context?.establishment?.siret || "—"} · SIREN {e.context?.establishment?.siren || "—"} · URL {e.context?.establishment?.website_url || "—"} · {e.context?.establishment?.city || ""}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {result.errors.length > 0 && (
                            <div className="bg-white border border-gray-200 rounded-xs p-4 mb-10">
                                <h2 className="text-sm font-semibold text-navy-700 dark:text-white mb-3">
                                    Erreurs (premières {Math.min(result.errors.length, 50)})
                                </h2>
                                <div className="space-y-2">
                                    {result.errors.slice(0, 50).map((e) => (
                                        <div key={`${e.rowNumber}-${e.message}`} className="rounded-xs border border-red-200 bg-red-50 p-3">
                                            <p className="text-xs font-medium text-red-700">Row {e.rowNumber}</p>
                                            <p className="text-xs text-red-700 mt-0.5">{e.message}</p>
                                            <p className="text-[11px] text-red-900/70 mt-1">
                                                Établissement: {e.context?.establishment?.name || "—"} · SIRET {e.context?.establishment?.siret || "—"} · SIREN {e.context?.establishment?.siren || "—"} · URL {e.context?.establishment?.website_url || "—"} · {e.context?.establishment?.city || ""}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
